class PredictiveSearch extends HTMLElement {
	constructor() {
		super()

		this.selectors = {
			input: '[data-predictive-search-input]',
			form: 'form',
			formStatus: '[data-predictive-search-status]',
			predictiveSearchResults: '[data-predictive-search]',
			closeBtn: '.f-drawer__close'
		}

		this.domNodes = queryDomNodes(this.selectors, this)
		this.drawer = this.closest('drawer-component')
		this.input = this.domNodes.input
		this.form = this.domNodes.form
		this.formStatus = this.domNodes.formStatus
		this.predictiveSearchResults = this.domNodes.predictiveSearchResults

		this.cachedResults = {}
		this.isOpen = false
		this.predictive_search_url = window.FoxThemeSettings.routes.predictive_search_url
		this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search')
		this.searchTerm = ''

		/* Empêche les anciennes recherches de réécrire les nouvelles */
		this.abortController = null
		this.requestId = 0

		this.setupEventListeners()
	}

	setupEventListeners() {
		this.form.addEventListener('submit', this.onFormSubmit.bind(this))

		this.input.addEventListener(
			'input',
			debounce((event) => {
				this.onChange(event)
			}, 300).bind(this)
		)

		this.input.addEventListener('focus', this.onFocus.bind(this))
		this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.addEventListener('keyup', this.onKeyup.bind(this))
		this.addEventListener('keydown', this.onKeydown.bind(this))

		if (this.domNodes.closeBtn) {
			this.domNodes.closeBtn.addEventListener('click', this.onClose.bind(this))
		}
	}

	getQuery() {
		return this.input.value.trim()
	}

	onChange() {
		const newSearchTerm = this.getQuery()

		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			this.querySelector('#f-predictive-search-results')?.remove()
		}

		this.updateSearchForTerm(this.searchTerm, newSearchTerm)

		this.searchTerm = newSearchTerm

		if (!this.searchTerm.length) {
			this.abortCurrentRequest()
			this.close(true)
			return
		}

		this.getSearchResults(this.searchTerm)
	}

	onFormSubmit(e) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a')
		) {
			e.preventDefault()
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery()

		if (!currentSearchTerm.length) return

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange()
		} else if (this.getAttribute('results') === 'true') {
			this.open()
		} else {
			this.getSearchResults(this.searchTerm)
		}
	}

	onFocusOut() {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) {
				this.close()
			}
		}, 100)
	}

	onClose() {
		this.abortCurrentRequest()
		this.close(true)
	}

	onKeyup(event) {
		if (!this.getQuery().length) {
			this.abortCurrentRequest()
			this.close(true)
		}

		switch (event.code) {
			case 'ArrowUp':
				event.preventDefault()
				this.switchOption('up')
				break

			case 'ArrowDown':
				event.preventDefault()
				this.switchOption('down')
				break

			case 'Enter':
				this.selectOption()
				break
		}
	}

	onKeydown(event) {
		if (
			event.code === 'ArrowUp' ||
			event.code === 'ArrowDown'
		) {
			event.preventDefault()
		}
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) return

		const moveUp = direction === 'up'
		const selectedElement = this.querySelector('[aria-selected="true"]')
		const allElements = this.querySelectorAll('li')
		let activeElement = this.querySelector('li')

		if (!activeElement || !allElements.length) return
		if (moveUp && !selectedElement) return

		if (this.formStatus) {
			this.formStatus.textContent = ''
		}

		if (!moveUp && selectedElement) {
			activeElement =
				selectedElement.nextElementSibling ||
				allElements[0]
		} else if (moveUp) {
			activeElement =
				selectedElement.previousElementSibling ||
				allElements[allElements.length - 1]
		}

		if (!activeElement || activeElement === selectedElement) return

		activeElement.setAttribute('aria-selected', 'true')

		if (selectedElement) {
			selectedElement.setAttribute('aria-selected', 'false')
		}

		this.setLiveRegionText(activeElement.textContent || '')

		if (activeElement.id) {
			this.input.setAttribute(
				'aria-activedescendant',
				activeElement.id
			)
		}
	}

	selectOption() {
		const selectedProduct = this.querySelector(
			'[aria-selected="true"] a, [aria-selected="true"] button'
		)

		if (selectedProduct) {
			selectedProduct.click()
		}
	}

	abortCurrentRequest() {
		if (this.abortController) {
			this.abortController.abort()
			this.abortController = null
		}
	}

	getSearchResults(searchTerm) {
		const cleanTerm = searchTerm.trim()

		if (!cleanTerm.length) return

		const queryKey = cleanTerm
			.toLowerCase()
			.replace(/\s+/g, '-')

		this.setLiveRegionLoadingState()

		/* Résultat déjà en cache */
		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(
				this.cachedResults[queryKey],
				cleanTerm
			)
			return
		}

		/* Annule la requête précédente */
		this.abortCurrentRequest()

		this.abortController = new AbortController()
		const signal = this.abortController.signal

		/* Numéro unique pour cette recherche */
		const currentRequestId = ++this.requestId

		const url =
			`${this.predictive_search_url}` +
			`?q=${encodeURIComponent(cleanTerm)}` +
			`&section_id=predictive-search`

		fetch(url, { signal })
			.then((res) => {
				if (!res.ok) {
					throw new Error(`Predictive search error: ${res.status}`)
				}

				return res.text()
			})
			.then((text) => {
				/*
				 * Si une nouvelle recherche a été lancée entre-temps,
				 * on ignore complètement cette ancienne réponse.
				 */
				if (currentRequestId !== this.requestId) return

				if (this.getQuery() !== cleanTerm) return

				const parsedDocument = new DOMParser().parseFromString(
					text,
					'text/html'
				)

				const section = parsedDocument.querySelector(
					'#shopify-section-predictive-search'
				)

				if (!section) {
					throw new Error(
						'Predictive search section not found'
					)
				}

				const resultsMarkup = section.innerHTML

				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						if (
							predictiveSearchInstance &&
							predictiveSearchInstance.cachedResults
						) {
							predictiveSearchInstance.cachedResults[
								queryKey
							] = resultsMarkup
						}
					}
				)

				this.renderSearchResults(
					resultsMarkup,
					cleanTerm
				)
			})
			.catch((error) => {
				/*
				 * AbortError est normal :
				 * cela signifie simplement qu'une nouvelle lettre
				 * a été tapée avant la fin de la précédente recherche.
				 */
				if (error.name === 'AbortError') return

				console.error(error)

				this.removeAttribute('loading')
			})
			.finally(() => {
				if (currentRequestId === this.requestId) {
					this.abortController = null
				}
			})
	}

	setLiveRegionLoadingState() {
		this.loadingText =
			this.loadingText ||
			this.getAttribute('data-loading-text') ||
			''

		this.setLiveRegionText(this.loadingText)
		this.setAttribute('loading', 'true')
	}

	setLiveRegionText(statusText) {
		if (!this.formStatus) return

		this.formStatus.setAttribute('aria-hidden', 'false')
		this.formStatus.textContent = statusText || ''

		clearTimeout(this.liveRegionTimer)

		this.liveRegionTimer = setTimeout(() => {
			if (this.formStatus) {
				this.formStatus.setAttribute(
					'aria-hidden',
					'true'
				)
			}
		}, 1000)
	}

	renderSearchResults(resultsMarkup, requestedTerm = '') {
		/*
		 * Vérification supplémentaire :
		 * ne jamais afficher le résultat d'un ancien terme.
		 */
		if (
			requestedTerm &&
			this.getQuery() !== requestedTerm
		) {
			return
		}

		if (!this.predictiveSearchResults) return

		this.predictiveSearchResults.innerHTML = resultsMarkup

		this.setAttribute('results', 'true')
		this.setLiveRegionResults()
		this.open()
	}

	setLiveRegionResults() {
		this.removeAttribute('loading')

		const resultCountElement = this.querySelector(
			'[data-predictive-search-live-region-count-value]'
		)

		if (resultCountElement) {
			this.setLiveRegionText(
				resultCountElement.textContent || ''
			)
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			'[data-predictive-search-search-for-text]'
		)

		if (!searchForTextElement) return

		const currentButtonText =
			searchForTextElement.innerText || ''

		if (!currentButtonText) return
		if (!previousTerm) return

		/*
		 * Correction importante :
		 * match() peut retourner null.
		 * L'ancien code faisait directement .length dessus.
		 */
		const escapedPreviousTerm =
			previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

		const matches =
			currentButtonText.match(
				new RegExp(escapedPreviousTerm, 'g')
			) || []

		if (matches.length > 1) {
			return
		}

		if (matches.length === 0) {
			return
		}

		searchForTextElement.innerText =
			currentButtonText.replace(
				previousTerm,
				newTerm
			)
	}

	open() {
		if (!this.getQuery().length) return

		this.setAttribute('open', 'true')
		this.input.setAttribute('aria-expanded', 'true')
		this.isOpen = true
	}

	close(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = ''
			this.searchTerm = ''
			this.removeAttribute('results')
		}

		const selected = this.querySelector(
			'[aria-selected="true"]'
		)

		if (selected) {
			selected.setAttribute(
				'aria-selected',
				'false'
			)
		}

		this.input.setAttribute(
			'aria-activedescendant',
			''
		)

		this.removeAttribute('open')
		this.input.setAttribute('aria-expanded', 'false')

		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.removeAttribute(
				'style'
			)
		}

		this.isOpen = false
	}
}

if (!customElements.get('predictive-search')) {
	customElements.define(
		'predictive-search',
		PredictiveSearch
	)
}